import styled from "@emotion/styled";
import { useMemo, useState } from "react";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";

import { MoreHoriz } from "@livechat/design-system-icons";
import { ActionMenu, ActionMenuItem, Button, IActionMenuOption, Icon } from "@livechat/design-system-react-components";

import PriorityIndicator from "components/priority/PriorityIndicator";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { getIsMemberOfTicketTeam } from "helpers/tickets";
import {
  ITicket,
  TicketActionTypes,
  TicketPriority,
  TicketSilo,
  TicketSiloFormattedValues,
  TicketStatus,
  TicketStatusFormattedValues,
} from "interfaces/entities/ticket";
import { selectIsAdmin, selectLoggedInAgent } from "store/entities/agents";
import { TicketActions } from "store/entities/tickets/actions";
import { TicketsViewActions } from "store/views/tickets/actions";
import { selectCanManageTickets } from "store/views/tickets/selectors";

import { DeleteTicketModal } from "../../DeleteTicketModal";

interface IProps {
  ticket: ITicket;
  onSpamModalRequest(type: "spam" | "unspam", selectedIds: string[]): void;
}

const TicketContextMenu: React.FC<IProps> = ({ ticket, onSpamModalRequest }) => {
  const dispatch = useDispatch();
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const currentAgent = useSelector(selectLoggedInAgent);
  const canManageTickets = useSelector(selectCanManageTickets);
  const isMemberOfTicketTeam = getIsMemberOfTicketTeam(currentAgent, ticket);
  const disabled = !canManageTickets || !isMemberOfTicketTeam;
  const isOwner = useSelector(selectIsAdmin);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleUpdatePriority = (newPriority: TicketPriority) => {
    if (ticket.priority === newPriority) {
      return;
    }

    dispatch(
      TicketsViewActions.updatePriority({ ticketId: ticket.id, priority: newPriority, interaction: "contextMenu" })
    );
    setDropdownVisible(false);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleStatusSelect = (newStatus: TicketStatus) => {
    if (ticket.status === newStatus) {
      return;
    }

    dispatch(
      TicketsViewActions.updateStatus({
        status: newStatus,
        ticketId: ticket.id,
        interaction: "contextMenu",
        usingShortcut: false,
      })
    );
    setDropdownVisible(false);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleUpdateSilo = (newSilo: TicketSilo) => {
    dispatch(TicketActions.updateSilo({ silo: newSilo, ticketId: ticket.id }));

    if (newSilo === TicketSilo.Spam) {
      trackEngagement("Ticket: Blocked list modal opened on context menu change", EventPlace.Tickets);
      onSpamModalRequest("spam", [ticket.id]);
    }

    if (newSilo !== TicketSilo.Spam && ticket.silo === TicketSilo.Spam) {
      trackEngagement("Ticket: Trusted list modal opened on context menu change", EventPlace.Tickets);
      onSpamModalRequest("unspam", [ticket.id]);
    }

    setDropdownVisible(false);
    trackEngagement("Tickets: Change silo actions", EventPlace.MoreOptions, {
      from: ticket.silo,
      to: newSilo,
      place: EventPlace.MoreOptions,
    });
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleDeleteForever = () => {
    dispatch(TicketsViewActions.deleteTicket(ticket.id));
    trackEngagement("Tickets: Delete forever actions", EventPlace.MoreOptions, {
      from: ticket.silo,
      place: EventPlace.MoreOptions,
    });
    setDeleteModalOpened(false);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleOpenDeleteModal = () => {
    setDeleteModalOpened(true);
    setDropdownVisible(false);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleCloseDeleteModal = () => {
    setDeleteModalOpened(false);
  };

  const defaultItems = useMemo<IActionMenuOption[]>(
    () => [
      ...[TicketPriority.Urgent, TicketPriority.High, TicketPriority.Medium, TicketPriority.Low].map(
        (priority, i, arr) => ({
          key: priority.toString(),
          element: (
            <ActionMenuItem leftNode={<PriorityIndicator priority={priority} />}>
              Set priority to {TicketPriority[priority]}
            </ActionMenuItem>
          ),
          // FIXME: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          onClick: () => handleUpdatePriority(priority),
          withDivider: i === arr.length - 1,
          disabled,
        })
      ),
      ...[TicketStatus.Open, TicketStatus.Pending, TicketStatus.OnHold, TicketStatus.Solved, TicketStatus.Closed].map(
        (status, i, arr) => ({
          key: status,
          element: <ActionMenuItem>Set as {TicketStatusFormattedValues[status]}</ActionMenuItem>,
          // FIXME: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          onClick: () => handleStatusSelect(status),
          disabled,
          withDivider: i === arr.length - 1,
        })
      ),
      ...[TicketSilo.Archive, TicketSilo.Spam, TicketSilo.Trash].map(silo => ({
        key: silo,
        element: (
          <ActionMenuItem kind={silo === TicketSilo.Trash ? "warning" : undefined}>
            Move to {TicketSiloFormattedValues[silo]}
          </ActionMenuItem>
        ),
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        onClick: () => handleUpdateSilo(silo),
        disabled,
      })),
    ],
    [disabled, ticket.priority, ticket.status]
  );

  const archiveItems = useMemo<IActionMenuOption[]>(
    () => [
      {
        key: TicketSilo.Tickets,
        element: <ActionMenuItem>Unarchive</ActionMenuItem>,
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        onClick: () => handleUpdateSilo(TicketSilo.Tickets),
        disabled,
      },
      {
        key: TicketSilo.Trash,
        element: <ActionMenuItem kind="warning">Move to {TicketSiloFormattedValues[TicketSilo.Trash]}</ActionMenuItem>,
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        onClick: () => handleUpdateSilo(TicketSilo.Trash),
        disabled,
      },
    ],
    [disabled]
  );

  const spamItems = useMemo(() => {
    const items = [
      {
        key: TicketSilo.Tickets,
        element: <ActionMenuItem>Not spam</ActionMenuItem>,
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        onClick: () => handleUpdateSilo(TicketSilo.Tickets),
        disabled,
      },
      {
        key: TicketActionTypes.DeleteForever,
        element: <ActionMenuItem kind="warning">Delete forever</ActionMenuItem>,
        onClick: handleOpenDeleteModal,
        disabled,
      },
    ];

    return isOwner ? items : items.slice(0, -1);
  }, [disabled, handleUpdateSilo, isOwner]);

  const trashItems = useMemo<IActionMenuOption[]>(() => {
    const items = [
      {
        key: TicketSilo.Tickets,
        element: <ActionMenuItem>Restore</ActionMenuItem>,
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        onClick: () => handleUpdateSilo(TicketSilo.Tickets),
        disabled,
      },
      {
        key: TicketActionTypes.DeleteForever,
        element: <ActionMenuItem kind="warning">Delete forever</ActionMenuItem>,
        onClick: handleOpenDeleteModal,
        disabled,
      },
    ];

    return isOwner ? items : items.slice(0, -1);
  }, [disabled, isOwner]);

  // returns items for dropdown based on silo and spam status, spam status condition is for backward compatibility - but it should be removed in the future
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const getItemsByTicketSilo = () => {
    if (ticket.silo === TicketSilo.Archive) {
      return archiveItems;
    }
    if (ticket.silo === TicketSilo.Spam || ticket.spam.status) {
      return spamItems;
    }
    if (ticket.silo === TicketSilo.Trash) {
      return trashItems;
    }
    return defaultItems;
  };

  return (
    <>
      {/* Workaround for options onClick prop not providing event argument */}
      <div onClick={e => e.stopPropagation()}>
        <ActionMenu
          selectedOptions={[ticket.priority.toString(), ticket.status]}
          options={getItemsByTicketSilo()}
          visible={dropdownVisible}
          onClose={() => setDropdownVisible(false)}
          triggerRenderer={
            <TriggerMenuButton
              id="popover-trigger-button"
              data-test={`ticket-${ticket.id}-context-menu`}
              icon={<Icon source={MoreHoriz} />}
              size="compact"
              kind="plain"
              // FIXME: Fix this the next time the file is edited.
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onClick={(e: any) => {
                e.stopPropagation();
                e.preventDefault();
                setDropdownVisible(prev => !prev);
              }}
            />
          }
        />
        {deleteModalOpened && <DeleteTicketModal onSubmit={handleDeleteForever} onClose={handleCloseDeleteModal} />}
      </div>
    </>
  );
};

const TriggerMenuButton = styled(Button)`
  margin-left: -8px;
`;

export default TicketContextMenu;
